import {
  Avatar,
  Box,
  Button,
  Grid,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import React from "react";
import { useUser } from "../contexts/useUser";
import { useQuery } from "@tanstack/react-query";
import { fetchLLMCustomers } from "../dataaccess/fetchLLMCustomers";
import { useNavigate } from "react-router-dom";

const Customers: React.FC = () => {
  const { project } = useUser();
  const navigate = useNavigate();

  const [nameFilter, setNameFilter] = React.useState<string | undefined>(
    undefined
  );
  const [tokensFilter, setTokensFilter] = React.useState<number | undefined>(
    undefined
  );

  const { data, isLoading, isError } = useQuery({
    queryKey: ["customers"],
    queryFn: () => fetchLLMCustomers(project?.id ?? ""),
    enabled: project?.id && project?.id.length > 0 ? true : false,
  });

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (isError) {
    return <div>Error...</div>;
  }

  const customers =
    data?.data?.customersByProjectIdAndLastCallMadeAt?.items ?? [];

  return (
    <>
      <Box p={2}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Paper elevation={3} style={{ borderRadius: 3, height: 100, border: "1px solid #444" }}>
              <Box
                display="flex"
                justifyContent="flex-start"
                alignItems="center"
                height="100%"
              >
                <TextField
                  label="Filter name"
                  variant="outlined"
                  onChange={(e) => setNameFilter(e.currentTarget.value)}
                  value={nameFilter}
                  size="small"
                  sx={{ marginLeft: 2, width: 300 }}
                />

                <TextField
                  label="Filter remaining tokens less than"
                  variant="outlined"
                  value={tokensFilter?.toString()}
                  onChange={(e) =>
                    setTokensFilter(
                      e.currentTarget.value
                        ? parseInt(e.currentTarget.value)
                        : undefined
                    )
                  }
                  size="small"
                  type="number"
                  sx={{ marginLeft: 2, width: 300 }}
                />
                <Button
                  variant="text"
                  sx={{ marginLeft: 2 }}
                  onClick={() => {
                    setNameFilter("");
                    setTokensFilter(undefined);
                  }}
                >
                  reset
                </Button>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Paper elevation={3} style={{ borderRadius: 3, border: "1px solid #444"}}>
              <Box height="100%">
                <List>
                  {customers
                    .filter((customer) => {
                      if (
                        nameFilter &&
                        customer?.name
                          ?.toLowerCase()
                          .indexOf(nameFilter.toLowerCase()) === -1
                      ) {
                        return false;
                      }

                      if (
                        tokensFilter &&
                        (customer?.tokensRemaining ?? 0) >= tokensFilter
                      ) {
                        return false;
                      }

                      return true;
                    })
                    .map((customer) => (
                      <ListItem
                        key={customer?.id}
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          cursor: "pointer",
                        }}
                        onClick={() => navigate(`edit/${customer?.id}`)}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            flexGrow: 1,
                          }}
                        >
                          <ListItemAvatar>
                            <Avatar
                              sx={{
                                bgcolor:
                                  customer?.status === "active"
                                    ? "primary.main"
                                    : "secondary.main",
                              }}
                            >
                              {customer?.numberOfCallsMade ?? 0}
                            </Avatar>
                          </ListItemAvatar>

                          <Box sx={{ marginLeft: 2 }}>
                            <ListItemText
                              primary={customer?.name}
                              secondary={`status: ${
                                customer?.status
                              }, last call made: ${
                                customer?.lastCallMadeAt
                                  ? new Date(
                                      customer?.lastCallMadeAt
                                    ).toLocaleString()
                                  : "no calls yet"
                              }, tokens remaining: ${
                                customer?.tokensRemaining ?? 0
                              } (last replenished: ${
                                customer?.lastReplenishedAt
                                  ? new Date(
                                      customer?.lastReplenishedAt
                                    ).toLocaleString()
                                  : "never"
                              })`}
                            />
                          </Box>
                        </Box>
                        <Box
                          sx={{ display: "flex", justifyContent: "flex-end" }}
                        >
                          <Button
                            variant="text"
                            onClick={() => navigate(`edit/${customer?.id}`)}
                          >
                            Edit
                          </Button>
                        </Box>
                      </ListItem>
                    ))}
                </List>
                <Button onClick={() => navigate("new")}>New Customer</Button>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

export default Customers;
